<template>
  <footer class="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
    <div class="container-custom py-12 md:py-16">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
        <!-- Brand Section -->
        <div class="md:col-span-2" data-test="footer-brand">
          <router-link to="/" class="inline-block mb-4">
            <h3 class="text-2xl font-bold">
              <span class="gradient-text">SecondChance</span><span class="text-gray-900 dark:text-white">.dev</span>
            </h3>
          </router-link>
          <p class="text-gray-600 dark:text-gray-400 mb-3 font-medium" data-test="footer-tagline">
            Your Skills. Your Future. Your Turn.
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-500 mb-4">
            A bias-free platform bringing modern AI skills and real tech careers to everyone the industry has overlooked. No stigma, no resume — just potential.
          </p>
          <!-- Email entreatment -->
          <div class="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-4 border border-primary-100 dark:border-primary-800/30">
            <p class="text-sm font-semibold text-primary-700 dark:text-primary-300 mb-1">We read every email.</p>
            <p class="text-xs text-gray-600 dark:text-gray-400 mb-2">
              Talent, employers, mentors, donors, partners — reach out. Whatever your reason, we want to hear from you.
            </p>
            <a
              href="mailto:hello@secondchance.dev"
              class="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              hello@secondchance.dev →
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div data-test="footer-links">
          <h4 class="font-semibold text-gray-900 dark:text-white mb-4">Quick Links</h4>
          <ul class="space-y-2">
            <li v-for="link in quickLinks" :key="link.path">
              <router-link
                :to="link.path"
                class="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                data-test="footer-link"
              >
                {{ link.text }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Connect Section -->
        <div>
          <h4 class="font-semibold text-gray-900 dark:text-white mb-4">Connect</h4>

          <!-- Contact Information -->
          <div class="mb-5 space-y-2 text-sm">
            <div class="text-gray-600 dark:text-gray-400">
              <div class="font-medium text-gray-700 dark:text-gray-300 mb-0.5">General</div>
              <a href="mailto:hello@secondchance.dev" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                hello@secondchance.dev
              </a>
            </div>
            <div class="text-gray-600 dark:text-gray-400">
              <div class="font-medium text-gray-700 dark:text-gray-300 mb-0.5">Support</div>
              <a href="mailto:support@secondchance.dev" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                support@secondchance.dev
              </a>
            </div>
            <div class="text-gray-600 dark:text-gray-400">
              <div class="font-medium text-gray-700 dark:text-gray-300 mb-0.5">Donate / Invest</div>
              <a href="mailto:donate@secondchance.dev" class="hover:text-rose-600 dark:hover:text-rose-400 transition-colors">
                donate@secondchance.dev
              </a>
            </div>
            <div class="text-gray-600 dark:text-gray-400">
              <div class="font-medium text-gray-700 dark:text-gray-300 mb-0.5">Partnerships</div>
              <a href="mailto:partners@secondchance.dev" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                partners@secondchance.dev
              </a>
            </div>
          </div>

          <div class="flex space-x-4" data-test="footer-social">
            <a
              v-for="social in socialLinks"
              :key="social.name"
              :href="social.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              :aria-label="`Follow us on ${social.name}`"
              data-test="social-link"
            >
              <!-- LinkedIn Icon -->
              <svg v-if="social.icon === 'linkedin'" class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <!-- Twitter/X Icon -->
              <svg v-else-if="social.icon === 'twitter'" class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <!-- GitHub Icon -->
              <svg v-else-if="social.icon === 'github'" class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
              </svg>
              <!-- Discord Icon -->
              <svg v-else-if="social.icon === 'discord'" class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
        <!-- ServiceVision Branding -->
        <div class="flex justify-center mb-5">
          <a
            href="https://servicevision.org"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-full border border-indigo-100 dark:border-indigo-800/30 text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
            <span>Empowered by <strong>ServiceVision.org</strong></span>
          </a>
        </div>

        <div class="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p class="text-sm text-gray-500 dark:text-gray-500" data-test="footer-copyright">
            © {{ currentYear }} SecondChance.dev. All rights reserved.
          </p>
          <div class="flex space-x-6 text-sm">
            <router-link to="/privacy" class="text-gray-500 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              Privacy Policy
            </router-link>
            <router-link to="/terms" class="text-gray-500 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              Terms of Service
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'

const currentYear = computed(() => new Date().getFullYear())

const quickLinks = [
  { path: '/about', text: 'About Us' },
  { path: '/for-talent', text: 'For Talent' },
  { path: '/for-employers', text: 'For Employers' },
  { path: '/pledge', text: 'Inclusive Hiring Pledge' },
  { path: '/for-mentors', text: 'Become a Mentor' },
  { path: '/programs', text: 'Our Programs' },
  { path: '/ai-skills-academy', text: 'AI Skills Academy' },
  { path: '/ai-incubator', text: 'AI Incubator' },
  { path: '/ai-upskilling', text: 'AI Upskilling' },
  { path: '/contact', text: 'Contact' }
]

const socialLinks = [
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/secondchance-dev',
    icon: 'linkedin'
  },
  {
    name: 'Twitter',
    url: 'https://twitter.com/SecondChanceDev',
    icon: 'twitter'
  },
  {
    name: 'GitHub',
    url: 'https://github.com/secondchance-dev',
    icon: 'github'
  },
  {
    name: 'Discord',
    url: 'https://discord.gg/secondchance-dev',
    icon: 'discord'
  }
]
</script>
